import { ChangeDetectionStrategy, Component, Input, computed, inject } from '@angular/core';

import { StatsService } from '../shared/services/stats.service';
import { formatMinutes } from '../shared/models/stats.model';

/**
 * "Your time saved" widget, bound to StatsService signals (the service owns
 * fetching — the chat component refreshes on boot and after each turn).
 *
 * Two variants:
 *  - 'strip': horizontal stat tiles under the welcome greeting (this month's
 *    time saved + sessions, all-time time saved). Hidden entirely until the
 *    user has at least one qualified session, so a first-run welcome stays clean.
 *  - 'card':  compact card for the sidebar user-menu (sibling of the usage
 *    meter) with a per-agent top-3 breakdown.
 *
 * Time saved is an ESTIMATE: each completed session credits the agent's
 * owner-configured minutes-per-session — the tooltip says so, and the same
 * numbers back the admin ROI dashboard, so surfaces always agree.
 */
@Component({
  selector: 'app-time-saved',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (variant === 'strip') {
      @if (hasAny()) {
        <div class="ts-strip" role="group" aria-label="Your time saved"
             title="Estimate: each completed agent session credits that agent's configured time-saved value.">
          <div class="ts-tile">
            <span class="ts-tile__value">{{ fmt(stats()!.this_month.time_saved_minutes) }}</span>
            <span class="ts-tile__label">saved this month</span>
          </div>
          <div class="ts-tile">
            <span class="ts-tile__value">{{ stats()!.this_month.sessions }}</span>
            <span class="ts-tile__label">sessions this month</span>
          </div>
          <div class="ts-tile">
            <span class="ts-tile__value">{{ fmt(stats()!.total.time_saved_minutes) }}</span>
            <span class="ts-tile__label">saved all-time</span>
          </div>
        </div>
      }
    } @else {
      <div class="ts-card" role="group" aria-label="Your time saved">
        <div class="ts-card__head">Time saved</div>
        @if (loading() && !stats()) {
          <span class="ts-card__muted">Loading…</span>
        } @else if (errored() && !stats()) {
          <span class="ts-card__muted">Stats unavailable</span>
        } @else if (stats(); as s) {
          <div class="ts-card__row">
            <span class="ts-card__label">This month</span>
            <span class="ts-card__val">{{ fmt(s.this_month.time_saved_minutes) }} · {{ s.this_month.sessions }} sessions</span>
          </div>
          <div class="ts-card__row">
            <span class="ts-card__label">All-time</span>
            <span class="ts-card__val">{{ fmt(s.total.time_saved_minutes) }} · {{ s.total.sessions }} sessions</span>
          </div>
          @if (sparkline(); as sp) {
            <div class="ts-card__spark" [title]="'Minutes saved per day, last 30 days (peak ' + sp.peak + 'm)'">
              <svg [attr.viewBox]="'0 0 ' + SPARK_W + ' ' + SPARK_H" preserveAspectRatio="none"
                   role="img" aria-label="Time saved per day, last 30 days">
                <polyline [attr.points]="sp.points" fill="none" stroke="currentColor"
                          stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
                <circle [attr.cx]="sp.lastX" [attr.cy]="sp.lastY" r="2" fill="currentColor" />
              </svg>
              <span class="ts-card__spark-label">last 30 days</span>
            </div>
          }
          @if (s.by_agent_top.length > 0) {
            <div class="ts-card__head ts-card__head--sub">Top agents</div>
            @for (a of s.by_agent_top.slice(0, 3); track a.agent_id) {
              <div class="ts-card__row">
                <span class="ts-card__label ts-card__label--agent">{{ a.display_name }}</span>
                <span class="ts-card__val">{{ fmt(a.time_saved_minutes) }}</span>
              </div>
            }
          }
          <div class="ts-card__note">
            Estimate — each completed session credits the agent's configured
            time-saved value.
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    /* ── Welcome strip ──────────────────────────────────────────── */
    .ts-strip {
      display: flex;
      gap: 10px;
      justify-content: center;
      flex-wrap: wrap;
      margin: 2px 0 10px;
    }
    .ts-tile {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1px;
      padding: 8px 16px;
      border-radius: 12px;
      border: 1px solid var(--ah-border, rgba(128, 128, 128, 0.22));
      background: var(--ah-surface-2, rgba(128, 128, 128, 0.06));
      min-width: 108px;
    }
    .ts-tile__value {
      font-size: 16px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: var(--ah-text-1, inherit);
    }
    .ts-tile__label {
      font-size: 11px;
      color: var(--ah-text-3, rgba(128, 128, 128, 0.9));
      white-space: nowrap;
    }

    /* ── User-menu card (mirrors usage-meter tokens) ────────────── */
    .ts-card {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 10px 12px;
      border-top: 1px solid var(--ah-border, rgba(128, 128, 128, 0.22));
      font-size: 12.5px;
    }
    .ts-card__head {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--ah-text-3, rgba(128, 128, 128, 0.9));
    }
    .ts-card__head--sub { margin-top: 4px; }
    .ts-card__row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 12px;
    }
    .ts-card__label { color: var(--ah-text-2, inherit); }
    .ts-card__label--agent {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 150px;
    }
    .ts-card__val {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      white-space: nowrap;
    }
    .ts-card__muted { color: var(--ah-text-3, rgba(128, 128, 128, 0.9)); }
    .ts-card__spark {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      color: var(--ah-accent, #c8102e);
    }
    .ts-card__spark svg {
      flex: 1;
      height: 28px;
      display: block;
    }
    .ts-card__spark-label {
      font-size: 10px;
      color: var(--ah-text-3, rgba(128, 128, 128, 0.75));
      white-space: nowrap;
    }
    .ts-card__note {
      font-size: 11px;
      line-height: 1.35;
      color: var(--ah-text-3, rgba(128, 128, 128, 0.75));
    }
  `],
})
export class TimeSavedComponent {
  @Input() variant: 'strip' | 'card' = 'card';

  private readonly statsService = inject(StatsService);

  readonly stats = this.statsService.stats;
  readonly loading = this.statsService.loading;
  readonly errored = this.statsService.errored;

  /** Strip renders only once there's something to celebrate. */
  readonly hasAny = computed(() => (this.stats()?.total.sessions ?? 0) > 0);

  readonly SPARK_W = 120;
  readonly SPARK_H = 28;

  /** 30-day minutes-saved sparkline geometry (null hides it: no data yet or
   *  an older API without by_day). Flat-zero series still renders — a flat
   *  line is honest information. */
  readonly sparkline = computed(() => {
    const days = this.stats()?.by_day;
    if (!days || days.length < 2) return null;
    const peak = Math.max(...days.map(d => d.time_saved_minutes), 1);
    const stepX = this.SPARK_W / (days.length - 1);
    const pad = 2;
    const scaleY = (v: number) =>
      this.SPARK_H - pad - (v / peak) * (this.SPARK_H - 2 * pad);
    const pts = days.map((d, i) =>
      `${(i * stepX).toFixed(1)},${scaleY(d.time_saved_minutes).toFixed(1)}`);
    const last = days.length - 1;
    return {
      points: pts.join(' '),
      lastX: (last * stepX).toFixed(1),
      lastY: scaleY(days[last].time_saved_minutes).toFixed(1),
      peak,
    };
  });

  readonly fmt = formatMinutes;
}
